import { useCallback } from 'react';
import { useCalibrateMutation } from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { useCalibrationWorkspace } from '../CalibrationWorkspaceContext';

const i18Path = 'performanceReview.calibration.saveBar';

export const useCalibrate = () => {
  const t = useNamespacedTranslation(i18Path);
  const { addSuccess, addWarning, addError } = useToasts();
  const {
    session,
    pendingChanges,
    clearPendingChanges,
    addConflict,
    removePendingChange,
  } = useCalibrationWorkspace();

  const [calibrateMutation, { loading }] = useCalibrateMutation();

  const calibrate = useCallback(async () => {
    const updates = Array.from(pendingChanges.values()).map((change) => ({
      employeeTopicId: change.employeeTopicId,
      rate: change.rate,
      expectedRate: change.expectedRate,
      notes: change.notes ?? null,
    }));

    try {
      const result = await calibrateMutation({
        variables: {
          input: {
            sessionId: session.Id,
            updates,
          },
        },
      });

      const payload = result.data?.calibration.calibrate;
      if (!payload) {
        addError(t('saveError'));
        return;
      }

      const errors = payload.updateErrors;
      if (errors.length > 0) {
        errors.forEach((err) => {
          addConflict(err.employeeTopicId);
          removePendingChange(err.employeeTopicId);
        });

        payload.updatedEmployeeTopicIds.forEach((id) => {
          removePendingChange(id);
        });

        addWarning(t('conflictWarning'));
        return;
      }

      clearPendingChanges();
      addSuccess(t('saveSuccess'));
    } catch {
      addError(t('saveError'));
    }
  }, [
    pendingChanges,
    calibrateMutation,
    session.Id,
    addSuccess,
    addWarning,
    addError,
    addConflict,
    clearPendingChanges,
    removePendingChange,
    t,
  ]);

  return { calibrate, loading };
};
